import {
  ABOLeg,
  ActualFormatted,
  EarningSummary,
  ECCalculateResponse,
  ECSettingsData,
  IncomeBreakdown,
  LevelSetting,
  Settings,
} from "../../../services/EarningsCalculator/earningCalculatorAPI.types";
import { ABOLegBody } from "../../earningCalculator";

export type ECComputedData = {
  EarningSummary: EarningSummary;
  IncomeBreakdown: IncomeBreakdown;
  ABOLegs: ABOLeg[];
  Settings: Settings;
  StaticData: {
    PersonalPV: ActualFormatted;
    VCSPV: ActualFormatted;
    IsBFIQualified: boolean;
    IsBBIQualified: boolean;
  };
};

export const getABOLegsWithGPVBase = (aboLegs: ABOLeg[]) => {
  return aboLegs.map((aboLeg) => ({
    Removeable: aboLeg.Removeable,
    AffABO: aboLeg.AffABO,
    GroupPV: aboLeg.GroupPV.Actual,
    GroupPVBase: aboLeg.GroupPV.Actual,
    Name: aboLeg.Name,
  }));
};

export const getSettings = (settings: Settings) => {
  const { CSI, BronzeBuilder, BronzeFoundation, BonusSchedule, PvBvRatio, IsExceededMaxValue, ABOLegs } = settings;
  return {
    IsExceededMaxValue,
    CSI: {
      Active: CSI.Active,
      Multiplier: CSI.Multiplier,
      ReqMinGPV: CSI.ReqMinGPV,
      MaxCSIPercent: CSI.MaxCSIPercent.Actual,
    },
    ABOLegs,
    BronzeBuilder,
    BronzeFoundation,
    BonusSchedule,
    PvBvRatio,
  };
};

export const getSettingsAPIData = (settingsAPIData: ECSettingsData) => {
  const { CurrentEarningSummary, CurrentIncomeBreakdown, CurrentABOLegs, Settings } = settingsAPIData;
  const { BronzeFoundation, BronzeBuilder } = CurrentIncomeBreakdown;
  return {
    EarningSummary: CurrentEarningSummary,
    IncomeBreakdown: CurrentIncomeBreakdown,
    ABOLegs: CurrentABOLegs,
    Settings: Settings,
    StaticData: {
      PersonalPV: CurrentEarningSummary.PersonalPV,
      VCSPV: CurrentEarningSummary.VCSPV,
      IsBFIQualified: BronzeFoundation.IsQualified,
      IsBBIQualified: BronzeBuilder.IsQualified,
    },
  };
};

export const getCalculateAPIData = (calculateAPIData: ECCalculateResponse) => {
  const { CalculatedEarningSummary, CalculatedABOLegs, CalculatedIncomeBreakdown } = calculateAPIData;

  return {
    EarningSummary: CalculatedEarningSummary,
    IncomeBreakdown: CalculatedIncomeBreakdown,
    ABOLegs: CalculatedABOLegs,
    StaticData: {
      PersonalPV: CalculatedEarningSummary.PersonalPV,
      VCSPV: CalculatedEarningSummary.VCSPV,
    },
  };
};

export const extractBronzeRequirements = (bronzeSetting: LevelSetting) => {
  const { Requirements } = bronzeSetting;
  const { LegsPercentage, VCSBonusLegs, PersonalVolume } = Requirements;

  return {
    targetVCSPV: VCSBonusLegs.TargetVCSPV,
    pvTarget: PersonalVolume.Target,
    legsTarget: LegsPercentage.Target,
    requiredGPV: LegsPercentage.RequiredGPV,
  };
};

export const isBronzeReqMet = (bronzeSetting: LevelSetting, earningSummary: EarningSummary, ABOLegs: ABOLeg[]) => {
  const { VCSPV, PersonalPV } = earningSummary;
  const { targetVCSPV, pvTarget, legsTarget, requiredGPV } = extractBronzeRequirements(bronzeSetting);
  const _ABOLegs = ABOLegs || []; // todo: api bug, remove this fallback after api fixed
  const legsReqMet = _ABOLegs.filter((leg) => leg.GroupPV.Actual >= requiredGPV);

  const isVCSPReqMet = VCSPV.Actual >= targetVCSPV;
  const isPPVReqMet = PersonalPV.Actual >= pvTarget;
  const isLegReqMet = legsReqMet.length >= legsTarget;
  return isLegReqMet && isVCSPReqMet && isPPVReqMet;
};

export const getFakeAffABONumber = () => {
  return Math.floor(10000 + Math.random() * 90000);
};

export const addNewLegs = (currentLegs: ABOLegBody[], legsTarget: number) => {
  const additionalLegs = legsTarget - currentLegs.length;

  if (additionalLegs > 0) {
    const newLegs: ABOLegBody[] = Array.from({ length: additionalLegs }, () => ({
      Removeable: true,
      AffABO: `${getFakeAffABONumber()}`,
      Name: {
        ABOName: "",
        ABOLocalName: "",
        ISOLanguage: "",
        PrivacyFlag: false,
      },
      GroupPV: 0,
      GroupPVBase: 0,
    }));
    return [...currentLegs, ...newLegs];
  }
  return currentLegs;
};

export const updateLegsGPV = (aboLegs: ABOLegBody[], legsTarget: number, requiredGPV: number) => {
  return aboLegs.map((leg, index) => {
    const groupPVBase = leg.GroupPVBase || 0;

    if (index < legsTarget && groupPVBase < requiredGPV) {
      return {
        ...leg,
        GroupPV: requiredGPV,
      };
    }

    return {
      ...leg,
      GroupPV: groupPVBase,
    };
  });
};

export const getBronzeTargetStatus = (settings: Settings, earningSummary: EarningSummary, aboLegs: ABOLeg[]) => {
  const { BronzeBuilder, BronzeFoundation } = settings;
  const isBBITabActive = isBronzeReqMet(BronzeBuilder, earningSummary, aboLegs);
  const isBFITabActive = !isBBITabActive && isBronzeReqMet(BronzeFoundation, earningSummary, aboLegs);

  return {
    IsBFITabActive: isBFITabActive,
    IsBBITabActive: isBBITabActive,
  };
};
